import { Maarays, MaaraysWithGroupedRajoitteet } from "@/koodistodatanTyypit";
import {
  assoc,
  assocPath,
  concat,
  filter,
  isEmpty,
  length,
  map,
  path,
  reject,
  isNil
} from "ramda";

const RAJOITE_ANCHOR_RX = /rajoitteet_([0-9a-fA-F-]{36})/;

// Try meta.rajoiteId; if missing, search changeObjects[].anchor
const getOrDeriveRajoiteId = (m: any): string | null => {
  const direct = path<string>(["meta", "rajoiteId"], m);
  if (direct) return String(direct);

  const cos = path<any[]>(["meta", "changeObjects"], m);
  if (Array.isArray(cos)) {
    for (const item of cos) {
      const a = path<string>(["anchor"], item);
      if (typeof a === "string") {
        const match = a.match(RAJOITE_ANCHOR_RX);
        if (match?.[1]) return match[1];
      }
    }
  }
  return null;
};

// Ensure meta.rajoiteId is set if we can derive it from anchors
const ensureRajoiteId = <T>(m: T): T => {
  const id = getOrDeriveRajoiteId(m);
  return id ? (assocPath(["meta", "rajoiteId"], id, m) as T) : m;
};

export const initializeRajoitteet = (
  maaraykset: Array<Maarays> = []
): Array<MaaraysWithGroupedRajoitteet> => {
  /** Groupataan alimääräykset rajoiteId:n mukaan ja asetetaan grouppauksen tulos aliMaaraykset propertyyn */
  const rajoitemaaraykset = reject(
    isNil,
    map((maarays: Maarays) => {
      // Make sure parent has rajoiteId if it can be derived
      const parent = ensureRajoiteId(maarays);

      // Normalize children and ensure each gets a rajoiteId if present in anchors
      const alimaaraykset: Array<Maarays> = (parent.aliMaaraykset || []).map(
        ensureRajoiteId
      );

      // Group ONLY children that have an id (no "" buckets)
      const groupedAlimaaraykset = alimaaraykset.reduce(
        (acc, child) => {
          const id = getOrDeriveRajoiteId(child);
          if (!id) return acc;
          (acc[id] ??= []).push(child);
          return acc;
        },
        {} as Record<string, Array<Maarays>>
      );

      return !isEmpty(groupedAlimaaraykset)
        ? assoc("aliMaaraykset", groupedAlimaaraykset, { ...parent })
        : null;
    }, maaraykset)
  ) as unknown as Array<MaaraysWithGroupedRajoitteet>;

  // opiskelijamäärä-määräykset without children (but must have an id, direct or derived)
  const opiskelijamaaramaaraykset = filter(
    maarays =>
      !length(maarays.aliMaaraykset) &&
      maarays.koodisto === "kujalisamaareet" &&
      path(["maaraystyyppi", "tunniste"], maarays) === "RAJOITE" &&
      Boolean(getOrDeriveRajoiteId(maarays)),
    maaraykset
  );

  /** Opiskelijamäärämääräys, jolla ei alimääräyksiä samaan muotoon kuin muut rajoitemääräykset */
  const opiskelijamaaramaarayksetModified = map(maarays => {
    const id = getOrDeriveRajoiteId(maarays);
    if (!id) {
      throw new Error(
        `Rajoite ID is missing for opiskelijamäärämääräys (koodisto=${maarays.koodisto}, koodiarvo=${maarays.koodiarvo})`
      );
    }
    const maaraysWithId = ensureRajoiteId(maarays);
    return {
      ...maaraysWithId,
      aliMaaraykset: { [id]: [ensureRajoiteId(maaraysWithId)] }
    };
  }, opiskelijamaaramaaraykset);

  return length(opiskelijamaaramaarayksetModified)
    ? concat(rajoitemaaraykset, opiskelijamaaramaarayksetModified)
    : rajoitemaaraykset;
};
